import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { useActions } from 'hooks/useActions'
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RootState } from 'dataStore/state'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'
import MenuList from './MenuList/index'
import { settings } from 'ThemeStyle'
import './Navbar.css'

interface DrawerProps {
  theme?: Theme
  open?: boolean
  drawerwidth: number
}

const openedMixin = (theme: Theme, drawerwidth: number): CSSObject => ({
  width: drawerwidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowY: 'hidden',
  border: 'none',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowY: 'hidden',
  width: 10,
  border: 'none',
  [theme.breakpoints.up('sm')]: {
    width: settings.sidebarClose,
  },
})

const PermanentDrawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open' && prop !== 'drawerwidth',
})<DrawerProps>(({ theme, open, drawerwidth: drawerWidth }) => ({
  width: drawerWidth || 280,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme, drawerWidth),
    '& .MuiDrawer-paper': openedMixin(theme, drawerWidth),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  border: 'none',
  ...theme.mixins.toolbar,
}))

const DrawerContent = styled('div')(({ theme }) => ({
  height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
}))

/**
 * Левое меню: на desktop — permanent collapse, на mobile — temporary overlay.
 */
const Sidebar = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const { sidebarState } = useSelector((state: RootState) => state.ui)
  const { setSidebar } = useActions()

  // на мобильном стартуем со скрытым меню
  useEffect(() => {
    if (isMobile) {
      setSidebar(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const handleDrawerToggle = () => {
    setSidebar(!sidebarState)
  }

  const handleClose = () => {
    setSidebar(false)
  }

  const menuContent = (
    <>
      <DrawerHeader>
        {(sidebarState || isMobile) && (
          <Typography
            variant='subtitle1'
            sx={{ pl: 1, fontWeight: 600, color: '#76A741' }}
            noWrap
          >
            Bruno Pizza
          </Typography>
        )}
        <IconButton
          onClick={isMobile ? handleClose : handleDrawerToggle}
          aria-label={sidebarState ? 'Свернуть меню' : 'Развернуть меню'}
        >
          {sidebarState || isMobile ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>
      <DrawerContent>
        <PerfectScrollbar
          options={{ suppressScrollX: true }}
          className='scrollbar'
          component='div'
          style={{
            height: '100%',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '16px',
          }}
        >
          <MenuList />
        </PerfectScrollbar>
      </DrawerContent>
    </>
  )

  if (isMobile) {
    return (
      <MuiDrawer
        variant='temporary'
        open={sidebarState}
        onClose={handleClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: settings.sidebarWidth,
            border: 'none',
          },
        }}
      >
        {menuContent}
      </MuiDrawer>
    )
  }

  return (
    <PermanentDrawer
      variant='permanent'
      open={sidebarState}
      drawerwidth={settings.sidebarWidth}
      sx={{ display: { xs: 'none', lg: 'block' } }}
    >
      {menuContent}
    </PermanentDrawer>
  )
}

export default Sidebar
